import React, { createContext, ReactNode, useContext, useEffect, useReducer } from 'react';
import sortBy from 'lodash/sortBy';

import { ContactsSyncApi } from '../api/ContactsSyncApi';
import { fetchHomepageData, getMergeSuggestions } from '../api/HomepageApi';
import { useErrorContext } from './ErrorContext';
import { useUserContext } from './UserContext';
import { appContactToKeyed, ContactsDatabase, IUDBKeyedContact } from '../lib/ContactsDatabase';
import ContactHelper from '../core/libs/ContactHelper';
import { hasContactId } from '../core/libs/utils';
import { ContactsReducer } from '../state/reducers/contacts';
import Search, { IUSearchItem } from '../cache/Search';

import { TAppMergeSuggestion } from 'TProtocol/homepage/messages';
import { TAppContact, TAuthHeader } from 'TProtocol/common/models';
import search from '../cache/Search';
import { TFetchContactsResponse } from 'TProtocol/contactssync/messages';
import { UAppContact } from '../models/UAppContact';
import { useModalContext } from './ModalContext';

export enum IUContactsActionType {
  Add = 1,
  Edit,
  Delete
}

export type IUContactsAction = [IUContactsActionType, TAppContact];

export interface IUContactsContext {
  clientModificationId: string | undefined;
  contacts: IUDBKeyedContact[];
  archivedContacts: IUDBKeyedContact[];
  getContact: (contactId?: string) => Promise<TAppContact | undefined>;
  selfContact: TAppContact | undefined;
  contactsLoaded: boolean;
  homepageLoaded: boolean;
  searchLoadProgress: number;

  visibleContactsMap: Map<string, TAppContact>;
  setVisibleContactsMap: (visibleContactsMap: Map<string, TAppContact>) => void;
  selectedContact: TAppContact | undefined;
  setSelectedContact: (contact: TAppContact | undefined) => void;
  contactToEdit: UAppContact | undefined;
  setContactToEdit: (contact: UAppContact | undefined) => void;
  updatingContact: TAppContact | undefined;

  LCM: Map<string, TAppContact[]> | undefined;
  setLCM: (LCM: Map<string, TAppContact[]>) => void;

  onDelete: (contact: TAppContact) => void;
  logout: () => void;
  onEditComplete: (contact: TAppContact) => void;

  updateContacts: (updateLocalContacts?: IUContactsAction) => void;
  syncContacts: () => void;
  reloadContacts: () => void;
  stopSyncing: () => void;
  continueSyncing: () => void;
  getImageUrl: (src: string) => Promise<string | undefined>,
  setImageUrl: (src: string, url: string) => Promise<void>;
}

export const ContactsContext = createContext<IUContactsContext | null>(null);

const db = new ContactsDatabase();

let allowSyncing = true;

export const stopSyncing = () => {
  allowSyncing = false;
};

export function ContactsContextProvider(props: { children: ReactNode }) {
  const errorContext = useErrorContext();
  const userContext = useUserContext();
  const modalContext = useModalContext();

  const [state, dispatch] = useReducer(ContactsReducer, {
    clientModificationId: undefined,
    contacts: [],
    archivedContacts: [],
    selfContact: undefined,
    contactsLoaded: false,
    homepageLoaded: false,
    searchLoadProgress: 0,
    LCM: new Map<string, TAppContact[]>(),
    visibleContactsMap: new Map<string, TAppContact>(),
    selectedContact: undefined,
    contactToEdit: undefined,
    updatingContact: undefined,
    error: false
  });

  db.setUserId(userContext.userId);

  async function modIdToDb(modId: string | undefined) {
    if (modId !== undefined) {
      try {
        await db.putModId(modId);
      } catch (e) {
        errorContext.error(e);
      }
    }
  }

  async function getModIdFromDb() {
    try {
      return db.getModId();
    } catch (e) {
      errorContext.error(e);
      return;
    }
  }

  const handleUpdateContactsToDB = async (res: IUDBKeyedContact[], updateLocalContacts: IUContactsAction) => {
    const updRes = res;
    let editedContact = null;
    let deletedContactId;

    if (updateLocalContacts.length > 0 && updateLocalContacts[0] === IUContactsActionType.Add) {
      const newContact = updateLocalContacts[1];
      if (hasContactId(newContact)) {
        updRes.push(appContactToKeyed(newContact));
        try {
          await db.updateContacts([newContact]);
        } catch (err) {
          errorContext.log(`Uh oh, contactsToDB error! ${err.stack}`);
        }
      }
    } else if (updateLocalContacts.length > 0 && updateLocalContacts[0] === IUContactsActionType.Edit) {
      editedContact = updateLocalContacts;
      try {
        await db.updateContacts([editedContact[1]]);
      } catch (err) {
        errorContext.log(`Uh oh, contactsToDB error! ${err.stack}`);
      }
    } else if (updateLocalContacts.length > 0 && updateLocalContacts[0] === IUContactsActionType.Delete) {
      deletedContactId = updateLocalContacts[1].contactId;
      if (deletedContactId) {
        try {
          await db.updateContacts([], false, [deletedContactId]);
        } catch (err) {
          errorContext.log(`Uh oh, contactsToDB error! ${err.stack}`);
        }
      }
    }
    return { updRes, editedContact, deletedContactId };
  };

  const logout = () => {
    db.close();
    db.delete();
    Search.clear();
    dispatch({ type: 'clear' });
    userContext.handleSignOut();
  };

  const getContact = async (contactId?: string) => {
    if (contactId === undefined) {
      return undefined;
    }
    return db.getContact(contactId);
  };

  const setContactToEdit = (contact?: TAppContact) => {
    dispatch({ type: 'editContact', contact });
  };

  const onEditComplete = async (contact: TAppContact) => {
    dispatch({ type: 'updatingContact', contact });

    ContactHelper.incrementClientEditSeqId(contact);

    let actionType = IUContactsActionType.Edit;

    if (state.contactToEdit && hasContactId(contact)) {
      try {
        const originalContact = await db.getContact(contact.contactId) ??
          (contact.contactId === state.selfContact?.contactId && state.selfContact);

        if (originalContact) {
          await ContactsSyncApi.editContact(userContext.getAuthHeader(), contact, originalContact);
          Search.updateItem(contact);
        } else {
          await ContactsSyncApi.addContact(userContext.getAuthHeader(), contact);
          actionType = IUContactsActionType.Add;
          Search.addItem(contact);
        }
        await updateContacts([actionType, contact]);
      } catch (e) {
        errorContext.log(e);
      }
    }

    dispatch({ type: 'updatedContact', contact, actionType });
  };

  const onDelete = async (contact: TAppContact) => {
    dispatch({ type: 'updatingContact', contact });
    try {
      await ContactsSyncApi.deleteContact(userContext.getAuthHeader(), contact);
      await updateContacts([IUContactsActionType.Delete, contact]);
      if (hasContactId(contact)) {
        Search.removeItem(contact.contactId);
      }
    } catch (e) {
      // do nothing for now
    }
    dispatch({ type: 'updatedContact', contact, actionType: IUContactsActionType.Delete });
  };

  const continueSyncing = () => {
    allowSyncing = true;
    void syncContacts();
  };

  useEffect(() => {
    if (state.contactsLoaded && state.contacts.length > 0 && !Search.initialized()) {
      void primeSearchFull(state.contacts, state.archivedContacts);
    }
  }, [state.contactsLoaded, state.contacts, state.archivedContacts]);

  const primeSearchFull = async (contacts: IUDBKeyedContact[], archivedContacts: IUDBKeyedContact[]) => {
    const len = contacts.length;
    let current = 0;
    let lastUpdated = 0;

    if (Search.initialized()) {
      // Search has already been initialized in fetchAllContacts from the db cache.
      return;
    }

    const searchItems: IUSearchItem[] = [];
    for (const keyedContact of [...contacts, ...archivedContacts]) {
      const contact = await db.getContact(keyedContact.contactId);
      if (contact !== undefined && hasContactId(contact)) {
        searchItems.push(Search.addItem(contact));
      }
      current++;
      const progress = Math.floor((current / len) * 100);
      if (progress > lastUpdated) {
        dispatch({ type: 'updateSearchProgress', progress });
        lastUpdated = progress;
      }
    }

    const index = search.getIndex();
    if (index !== undefined) {
      await db.setFuseCache({
        contacts: searchItems,
        index
      });
    }
  };

  const loadLCMData = async () => {
    let error = false;
    let LCM = state.LCM;

    try {
      const success = await fetchHomepageData(userContext.getAuthHeader());
      const merges = getMergeSuggestions(success);

      if (merges) {
        const suggestions = new Map<string, TAppContact[]>();

        merges.forEach((merge: TAppMergeSuggestion) => {
          if (merge.appContact1?.contactId && merge.appContact2?.contactId) {
            const appContacts = [merge.appContact1, merge.appContact2];
            suggestions.set(merge.mergeSuggestionId, appContacts);
          }
        });
        LCM = suggestions;
      } else {
        errorContext.error('Error fetching homepage data: No mergeSuggestionData');
        error = true;
      }
    } catch (e) {
      errorContext.error('Automated error fetching homepage data.', e);
      error = true;
    }

    dispatch({ type: 'homepageComplete', error, LCM });
  };

  const onLoadSuccess = async (successContacts: IUDBKeyedContact[], archivedContacts: IUDBKeyedContact[]) => {
    const selfContact = await db.getContact(userContext.selfContactId);

    const contacts: IUDBKeyedContact[] = [];
    successContacts.forEach(
      (c) => {
        if (c.contactId == userContext.selfContactId) {
          return;
        } else {
          contacts.push(c);
        }
      }
    );

    dispatch({ type: 'loadSuccess', contacts, selfContact, archivedContacts });
  };

  const onLoadComplete = async (allContacts: IUDBKeyedContact[], clientModificationId: string | undefined) => {
    let { contacts } = await sortContactsAndInsert(allContacts);

    contacts = contacts.filter(
      (c) => c.contactId !== userContext.selfContactId && !c.archived
    );

    dispatch({ type: 'loadComplete', contacts, clientModificationId });

    if (allowSyncing) {
      await loadLCMData();
    }
  };

  // ERROR
  const onLoadError = () => {
    dispatch({ type: 'error' });
  };

  const generateExistingContactMap = () => {
    const map = new Map<string, IUDBKeyedContact>();
    const archivedMap = new Map<string, IUDBKeyedContact>();
    state.contacts.forEach((contact) => {
      if (contact.contactId) {
        if (contact.archived) {
          archivedMap.set(contact.contactId, contact);
        } else {
          map.set(contact.contactId, contact);
        }
      }
    });
    return [map, archivedMap];
  };

  const fetchAllContacts = async (authHeader: TAuthHeader, clientModId?: string): Promise<void> => {
    let clientModificationId: string | undefined = clientModId;
    let previousClientModificationId: string | undefined | null = null;
    const [contactMap, archivedContactMap] = generateExistingContactMap();

    const cache = await db.getFuseCache();
    if (cache) {
      Search.createFromCache(cache);
    }

    const maxPageSize = 2000;
    let lastPage = false;
    let allContacts: IUDBKeyedContact[] = [];
    let archivedContacts: IUDBKeyedContact[] = [];

    async function processContactsResponse(response: TFetchContactsResponse) {
      if (response.contacts.length < maxPageSize) {
        lastPage = true;
      }
      previousClientModificationId = clientModificationId;
      clientModificationId = response.newClientModificationId;

      if (response.contacts) {
        // Create a map of contacts, so we can easily delete ones that the server tells us to
        response.contacts.forEach(contact => {
          if (hasContactId(contact)) {
            const contactId = contact.contactId;
            if (!contact.archived) { // only add non-hidden contacts
              contactMap.set(contactId, appContactToKeyed(contact));
            } else {
              contactMap.delete(contactId);
              archivedContactMap.set(contactId, appContactToKeyed(contact));
            }
            if (state.visibleContactsMap.get(contactId)) {
              state.visibleContactsMap.set(contactId, contact);
            }
            if (cache?.contacts.length) {
              // We already have a cached Fuse index, so we need to incrementally update these contacts as they come in.
              Search.updateItem(contact);
            }
          }
        });

        response.deletedContactIds.forEach(deletedContactId => {
          if (contactMap.has(deletedContactId)) {
            contactMap.delete(deletedContactId);
          }
          Search.removeItem(deletedContactId);
        });

        try {
          await db.updateContacts(response.contacts, false, response.deletedContactIds);
        } catch (err) {
          errorContext.error(`Uh oh, contactsToDB error! ${err.stack}`);
        }

        try {
          await modIdToDb(clientModificationId);
        } catch (err) {
          errorContext.error(`Uh oh, modId error! ${err.stack}`);
        }

        // Convert the map of contacts back into an array, so we can set it in the ContactsCache
        allContacts = Array.from(contactMap).map(([, contact]) => contact);
        archivedContacts = Array.from(archivedContactMap).map(([, contact]) => contact);
        const { contacts } = await sortContactsAndInsert(allContacts);
        await onLoadSuccess(contacts, archivedContacts);
      } else {
        errorContext.warning('No contacts found');
      }
    }

    // fetch first request
    let response = await ContactsSyncApi.fetchContacts(authHeader, clientModificationId, maxPageSize);
    // Iterate through chunks of contacts until we run out
    while (!lastPage && (clientModificationId !== previousClientModificationId || !clientModificationId)) {
      // Start on fetching the next page while we process the previous one.
      await Promise.all([
        processContactsResponse(response),
        (async () => {
          response = await ContactsSyncApi.fetchContacts(authHeader, clientModificationId, maxPageSize);
        })()
      ])
    }

    if (clientModId !== undefined) {
      // this was a partial load, so we need to do a full load from the DB
      allContacts = await db.getContacts();
    }

    await onLoadComplete(allContacts, clientModificationId);
  };

  const fetchFromApi = async (modId?: string) => {
    if (!userContext.isLoggedIn()) {
      return;
    }
    try {
      await fetchAllContacts(userContext.getAuthHeader(), modId);
    } catch (error) {
      errorContext.error('Error loading contacts:', error);
      return onLoadError();
    }
  };

  const openDb = async () => {
    try {
      await db.open();
    } catch (err) {
      if (err.name === 'InvalidStateError') {
        modalContext.showModal({
          title: 'Local Database Error',
          isError: true,
          showCancel: false,
          contents: <div>
            <p>An error occurred initializing the local database.</p>
            <p>If you are browsing in a private window, your browser may be preventing this. Sunshine Smart Contacts
              uses IndexedDB to cache your contacts locally to provide a better browsing experience. You will need to
              browse in a non-private window for Sunshine Smart Contacts to work.</p>
          </div>
        })
      } else {
        errorContext.error(`Failed to open db: ${err.stack || err}`);
      }
    }
  };

  const updateContacts = async (updateLocalContacts?: IUContactsAction) => {
    if (!allowSyncing) {
      return;
    }

    await openDb();

    try {
      const res: IUDBKeyedContact[] = await db.getContacts();
      if (!res || res.length == 0) {
        await fetchFromApi();
      } else if (updateLocalContacts) {
        const { updRes } = await handleUpdateContactsToDB(res, updateLocalContacts);
        const { contacts } = await sortContactsAndInsert(updRes);
        await onLoadSuccess(contacts, state.archivedContacts);
      }
    } catch (e) {
      return onLoadError();
    }
  };

  const sortContactsAndInsert = async (contactListWrapper: IUDBKeyedContact[]) => {
    let contacts = sortBy(contactListWrapper,
      [
        i => i.fullName === '',
        (contact) => {
          return contact.fullName.toLowerCase();
        }
      ]
    );

    contacts = contacts.filter(
      (c) => c.contactId !== userContext.selfContactId && !c.archived
    );

    return { contacts };
  };

  const syncContacts = async () => {
    if (!allowSyncing) {
      return;
    }

    await openDb();

    try {
      const res: IUDBKeyedContact[] = await db.getContacts();
      if (!res || res.length == 0) {
        await fetchFromApi();
      } else {
        await fetchFromApi(await getModIdFromDb());
      }
    } catch (e) {
      return onLoadError();
    }
  };

  const reloadContacts = async () => {
    if (!allowSyncing) {
      return;
    }

    await openDb();

    await fetchFromApi();
  };

  const getImageUrl = async (src: string) => {
    const res = await db.getImage(src);

    return res?.url;
  };

  const setImageUrl = async (src: string, url: string) => {
    await db.putImage(src, url);
  };

  const setVisibleContactsMap = (visibleContactsMap: Map<string, TAppContact>) => {
    dispatch({ type: 'setVisibleContactsMap', visibleContactsMap });
  }

  const setSelectedContact = (selectedContact: TAppContact) => {
    dispatch({ type: 'selectContact', selectedContact });
  };

  const setLCM = (LCM: Map<string, TAppContact[]>) => {
    dispatch({ type: 'setLCM', LCM });
  };

  useEffect(() => {
    void syncContacts();
  }, []);

  const context: IUContactsContext = {
    clientModificationId: state.clientModificationId,
    contacts: state.contacts,
    archivedContacts: state.archivedContacts,
    getContact,
    selfContact: state.selfContact,
    contactsLoaded: state.contactsLoaded,
    homepageLoaded: state.homepageLoaded,
    searchLoadProgress: state.searchLoadProgress,

    visibleContactsMap: state.visibleContactsMap,
    setVisibleContactsMap,
    selectedContact: state.selectedContact,
    setSelectedContact,
    contactToEdit: state.contactToEdit,
    setContactToEdit,
    updatingContact: state.updatingContact,
    LCM: state.LCM,
    setLCM,

    onDelete,
    logout,
    onEditComplete,

    updateContacts,
    syncContacts,
    reloadContacts,
    stopSyncing,
    continueSyncing,
    getImageUrl,
    setImageUrl
  };

  return (
    <ContactsContext.Provider value={context}>
      {props.children}
    </ContactsContext.Provider>
  );
}

export const useContactsContext = () => {
  const context = useContext(ContactsContext);
  if (context === null) {
    throw new Error('useContactsContext must be used within a ContactsContextProvider');
  }
  return context;
};
